import { map } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useCallback } from 'react';

import { cn } from 'utils/Tailwind';

const TableContent = React.forwardRef(({ data, handleKeyDown }, ref) => (
  <div
    data-testid="size-chart-table"
    ref={ref}
    role="grid"
    aria-labelledby="size-chart-heading"
    tabIndex="0"
    onKeyDown={handleKeyDown}
    className="no-scrollbar relative max-w-fit overflow-auto"
  >
    <div className="relative inline-flex min-w-full flex-col">
      {map(data, (row, rowIndex) => (
        <div key={rowIndex} className="flex" role="row" data-testid={`chart-row-${rowIndex}`}>
          {map(row, (cell, columnIndex) => (
            <div
              key={columnIndex}
              role={rowIndex === 0 ? 'columnheader' : columnIndex === 0 ? 'rowheader' : 'gridcell'}
              className={cn(
                'flex items-center justify-center p-4 text-center text-sm ',
                columnIndex === 0
                  ? 'sticky left-0 z-10 w-36 border-r border-solid border-neutral-600 font-bold shadow-2xl sm:w-40 lg:w-44'
                  : 'aspect-square w-20 md:aspect-[1/.57] lg:w-28',
                columnIndex % 2 === 0 ? 'bg-neutral-500' : 'bg-white',
                columnIndex !== row.length - 1 && 'border-r border-solid border-neutral-600',
                rowIndex !== data.length - 1 && 'border-b border-solid border-neutral-600'
              )}
            >
              {cell.value}
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
));

const Scrollbar = React.forwardRef(({ handleScrollbarMouseDown }, ref) => {
  const { scrollbarRef, scrollIndicatorRef } = ref;
  return (
    <div
      className="relative mt-4 h-2 w-full cursor-pointer overflow-hidden rounded-full bg-neutral-500"
      data-testid="size-chart-scrollbar"
      onMouseDown={handleScrollbarMouseDown}
      ref={scrollbarRef}
      role="scrollbar"
    >
      <div
        className="absolute left-0 top-0 h-full w-full rounded-full bg-neutral-700"
        data-testid="size-chart-scrollbar-indicator"
        ref={scrollIndicatorRef}
      />
    </div>
  );
});

const Chart = ({ data, heading, className, ...props }) => {
  const [isScrollable, setIsScrollable] = useState(false);
  const contentRef = useRef(null);
  const scrollIndicatorRef = useRef(null);
  const scrollbarRef = useRef(null);
  const isDraggingRef = useRef(false);

  const updateScrollIndicator = useCallback(() => {
    if (contentRef.current && scrollIndicatorRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = contentRef.current;
      const scrollPercentage = scrollLeft / (scrollWidth - clientWidth);
      const indicatorWidth = (clientWidth / scrollWidth) * 100;
      const newLeft = scrollPercentage * (100 - indicatorWidth);

      scrollIndicatorRef.current.style.width = `${indicatorWidth}%`;
      scrollIndicatorRef.current.style.left = `${newLeft}%`;

      const newIsScrollable = scrollWidth > clientWidth;
      if (newIsScrollable !== isScrollable) {
        setIsScrollable(newIsScrollable);
      }
    }
  }, [isScrollable]);

  const handleScrollbarInteraction = useCallback(
    clientX => {
      if (contentRef.current && scrollbarRef.current) {
        const { left, width } = scrollbarRef.current.getBoundingClientRect();
        const clickPosition = (clientX - left) / width;
        const newScrollLeft =
          clickPosition * (contentRef.current.scrollWidth - contentRef.current.clientWidth);
        contentRef.current.scrollLeft = newScrollLeft;
        updateScrollIndicator();
      }
    },
    [updateScrollIndicator]
  );

  const handleScrollbarMouseDown = useCallback(
    event => {
      isDraggingRef.current = true;
      handleScrollbarInteraction(event.clientX);
    },
    [handleScrollbarInteraction]
  );

  const handleScrollbarMouseMove = useCallback(
    event => {
      if (isDraggingRef.current) {
        handleScrollbarInteraction(event.clientX);
      }
    },
    [handleScrollbarInteraction]
  );

  const handleScrollbarMouseUp = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  const handleKeyDown = event => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const scrollAmount = event.key === 'ArrowRight' ? 100 : -100;
      if (contentRef.current) {
        contentRef.current.scrollLeft += scrollAmount;
      }
    }
  };

  useEffect(() => {
    const checkScrollable = () => {
      if (contentRef.current) {
        const { scrollWidth, clientWidth } = contentRef.current;
        setIsScrollable(scrollWidth > clientWidth);
      }
    };

    checkScrollable();
    updateScrollIndicator();

    window.addEventListener('resize', checkScrollable);
    window.addEventListener('resize', updateScrollIndicator);
    document.addEventListener('mousemove', handleScrollbarMouseMove);
    document.addEventListener('mouseup', handleScrollbarMouseUp);

    return () => {
      window.removeEventListener('resize', checkScrollable);
      window.removeEventListener('resize', updateScrollIndicator);
      document.removeEventListener('mousemove', handleScrollbarMouseMove);
      document.removeEventListener('mouseup', handleScrollbarMouseUp);
    };
  }, [updateScrollIndicator, handleScrollbarMouseMove, handleScrollbarMouseUp]);

  useEffect(() => {
    const contentEl = contentRef.current;
    if (contentEl) {
      contentEl.addEventListener('scroll', updateScrollIndicator);
    }
    return () => {
      if (contentEl) {
        contentEl.removeEventListener('scroll', updateScrollIndicator);
      }
    };
  }, [updateScrollIndicator]);

  return (
    <div data-testid="size-chart" className={cn('mb-6 max-w-full', className)} {...props}>
      <div className={cn('relative max-w-fit')}>
        {heading && (
          <h2
            id="size-chart-heading"
            className="mb-3 text-base font-bold uppercase tracking-widest"
          >
            {heading}
          </h2>
        )}
        <div className="relative max-w-fit overflow-hidden border border-solid border-neutral-600">
          <TableContent ref={contentRef} data={data} handleKeyDown={handleKeyDown} />
        </div>
        {isScrollable && (
          <Scrollbar
            ref={{ scrollbarRef, scrollIndicatorRef }}
            handleScrollbarMouseDown={handleScrollbarMouseDown}
          />
        )}
      </div>
    </div>
  );
};

TableContent.propTypes = {
  data: PropTypes.array.isRequired,
  handleKeyDown: PropTypes.func.isRequired,
};

Scrollbar.propTypes = {
  handleScrollbarMouseDown: PropTypes.func.isRequired,
};

Chart.propTypes = {
  category: PropTypes.string,
  className: PropTypes.string,
  data: PropTypes.arrayOf(
    PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string.isRequired }))
  ).isRequired,
  heading: PropTypes.string,
};

Chart.defaultProps = {
  category: null,
  className: null,
  heading: null,
};

export default Chart;
